import React, { useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import DOMPurify from 'dompurify';
import { Coachmark as  StorybookCoachmark} from '@sunbeltrentals/sbr-global-components';
import { useCartState } from '../../contexts/cart';
import { useUserContext } from '../../aem-core-components/context/UserContext';
import { useAnalyticsContext } from '../../config/GoogleTagManagerEvents';
import { useCartOperations } from './hooks/useCart';
import { useFilterState } from '../cap';
import useCheckoutNavigate from '../App/hooks/useCheckoutNavigate';
import { useCheckAuthorityType, useCheckUser } from '../../hooks/useCheckUser';
import useCheckLocationEmpty from '../../hooks/useCheckLocationEmpty';
import useProductTile from '../../hooks/useProductTile';
import useConsumables from '../../hooks/useConsumables';
import { useAtp } from '../cap/hooks/useATP';
import useCartAnalytics from '../../hooks/analytics/useCartAnalytics';
import useModalAnalytics from '../../hooks/analytics/useModalAnalytics';
import useToggle from '../../hooks/useToggle';
import useAnalytics from '../../hooks/analytics/useAnalytics';
import {
    AvsModal,
    PartialItemsUnavailableModal,
    CartDrawerGuestCheckout,
    EmptyCartView,
    CartOrderSummary,
    DeliverySlotAlertModal,
    AllItemsUnavailableModal,
    CartItems,
    ChronosStoreSelectorDrawer,
    Alert,
    AlertModalWithButtons,
    RentalEquipmentView
} from './lazyLoad/LazyLoadCartImports';
import LazyLoadComponent from '../global/lazyLoad/LazyLoadComponent';
import CartFooter from './components/cartFooter/CartFooter';
import CartPageShimmer from './cartShimmer/CartPageShimmer';
import { LoadingIndicator } from '../../aem-core-components';
import { ModalContext } from '../../aem-core-components/context/ModalContext';
import { getCartAemAuthorableDetails } from './utils/helper';
import { isValidString, logError } from '../global/utils/logger';
import { isAccountClosed, showWeekendRentalAlert } from '../global/utils/commonUtils';
import isObjectWithKeys from '../../aem-core-components/utils/isObjectWithKeys';
import AlertIcon from '../../resources/images/alert-triangle-red.svg';
import AlertRightCircle from '../../resources/images/alert-circle-right-green.svg';
import './cart.scss';
import { STORAGE_CONFIG } from '../../constants/storageConfig';
import { AUTHORABLE_FALLBACK, CART_AEM_CONFIG_VALUES } from './utils/constants';
import {
    SET_CART_ITEM_STATUS_CHANGED_REASON,
    SET_IS_CART_LOADING,
    SET_IS_CONSUMABLES_COACHMARK_OPEN,
    SET_IS_ESTIMATE_UPDATE_REQUIRED,
    SET_LOCATION_DATA,
    SET_RECOMPUTE_ITEM_AVAILABILITY,
    SET_SHOW_DELINQUENT_ACCOUNT_MODAL
} from '../../aem-core-components/actions/constants';
import { SET_SELECTED_STORE_DETAILS } from '../cap/constants';
import { USER_TYPE } from '../../constants/userDetailsConstants';
import { AUTHORITY_TYPE } from '../global/constants';
import { VARIABLE_CONFIG } from '../../constants/analyticsConstants/Variables';
import { CHOOSE_STORE } from '../../aem-core-components/context/Modal/constants';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../constants/analyticsConstants/Ecommerce';
import { ENV_CONFIG } from '../../constants/envConfig';
import config from '../App/config';

const Cart = () => {
    const intl = useIntl();
    const [
        {
            cart,
            cartId,
            isCartLoading,
            recomputeItemsAvailability,
            userAccount,
            isEstimateUpdateRequired,
            isConsumablesCoachmarkOpen,
            consumables
        },
        dispatch
    ] = useCartState();
    const [userState] = useUserContext();
    const { isProfileLoaded } = userState;
    const {
        renderCartItems,
        handleCartQuantityUpdation,
        sortedChronosPcs,
        getSelectedAndSuggestedStores,
        onCheckoutHandler,
        isCheckoutDetailsValidating,
        showDiscrepencyStoreSelector,
        updateItemsFromSelectedChronosStore,
        recomputeEstimates,
        isWithin72Hours,
        handleChronosStores,
        getCartItemsForChronosStoresWithAtp,
        updateFulfillmentToPickup,
        saveCartItemsInStorage,
        fetchChronosForInStorePickup
    } = useCartOperations({});
    const { updateSourceAndLocationSearchRadius, validateTimezoneAPI } = useCheckoutNavigate();
    const [{ viewCart, projectDetails, startDate, endDate, selectedStoreDetails }, filterDispatch] = useFilterState();
    const { isRentalDetailsAvailable } = useCheckLocationEmpty();
    const [{ getSelectedConsumablesAvailability, isAvailableOptionalItemsNotSelected, bulkUpdateConsumables }] = useConsumables();
    const { openEmptyLocationDateModal } = useProductTile();
    const userType = useCheckUser();
    const authorityType = useCheckAuthorityType();
    const { handleATPCart } = useAtp();
    const useModalState = () => useContext(ModalContext);
    const { openModal } = useModalState();
    const { sendEventsForViewItemList, sendEventsForCtaClicked } = useAnalyticsContext();
    const [{ payloadEcommerceViewItemList }] = useAnalytics();
    const [{ triggerCartViewedEvent, triggerCheckoutStartEvent, triggerEventForViewDetailsCta }] = useCartAnalytics();
    const [{ triggerModalViewedEvent, triggerAvsModalCtaEvent, triggerModalCancelEvent }] = useModalAnalytics();
    const [selectedLocationStore, setSelectedLocationStore] = useState(null);
    const [suggestedChronosStores, setSuggestedChronosStores] = useState([]); // it will store all the chronos stores except the selected store
    const [isCheckoutDrawerOpen, setIsCheckoutDrawerOpen] = useState(false);
    const [showStoreSelector, setShowStoreSelector] = useState(false);
    const [isCartInitialised, setIsCartInitialised] = useState(false);
    const [unavailableItemsArr, setUnavailableItemsArr] = useState([]);
    const [conditionallyAvailableArr, setConditionallyAvailableArr] = useState([]);
    const [availableItemArr, setAvailableItemArr] = useState([]);
    const [showDeliverySlotModal, setShowDeliverySlotModal] = useState(false);
    const [availableStartDate, setAvailableStartDate] = useState('');
    const [noAvailablePickupDate, setNoAvailablePickupDate] = useState('');
    const [showAvsModal, setShowAvsModal] = useState(false);
    const [unavailableItemsFromSources, setUnavailableItemsFromSources] = useState([]);
    const [isAllItemsUnavailableModalOpen, setIsAllItemsUnavailableModalOpen] = useState(false);
    const [scrollToUnavailableSection, setScrollToUnavailableSection] = useState(false);
    const [triggerAnalyticsCartEvent, setTriggerAnalyticsCartEvent] = useState(false);
    const isEmpty = !cart?.items?.length;
    const abortControllerRef = useRef(null);
    const accessoriesAddonsCoachmark = useRef(null);
    const [recomputeSourcesOnContinueCheckout, setRecomputeSourcesOnContinueCheckout] = useState(false);
    const [isAdditionalConsumableModalOpen, toggleIsAdditionalConsumableModalOpen] = useToggle(false);
    const isP2PUser = authorityType === AUTHORITY_TYPE.P2P;
    const cartAemConfigurationsData = ENV_CONFIG.CART_CONFIGURATIONS;

    const cartGenericProperties = JSON.parse(
        getCartAemAuthorableDetails(CART_AEM_CONFIG_VALUES.GENERIC_PROPERTIES, cartAemConfigurationsData) || '{}'
    );
    const cartAlertProperties = JSON.parse(
        getCartAemAuthorableDetails(CART_AEM_CONFIG_VALUES.ALERTS_PROPERTIES, cartAemConfigurationsData) || '{}'
    );
    const shouldShowAlertMessage = JSON.parse(
        cartAlertProperties?.enableAlertMessage || AUTHORABLE_FALLBACK?.enableAlertMessage
    );
    const accessoriesProperties = JSON.parse(
        getCartAemAuthorableDetails(CART_AEM_CONFIG_VALUES.ACCESSORIES_PROPERTIES, cartAemConfigurationsData) || '{}'
    );
    let additionalPayload;
    useEffect(() => {
        let capObserver;
        let scrollWatcher;

        try {
            const capHeader = document.querySelector('.capheader');
            const header = document.querySelector('header');
            if (!capHeader || !header) throw new Error('Required elements not found.');

            scrollWatcher = document.createElement('div');
            scrollWatcher.setAttribute('data-scroll-watcher', '');
            header.after(scrollWatcher);

            // Function to adjust the top position of the cap header
            const adjustHeaderPosition = () => {
                const punchoutBanner = document.querySelector('.punchout_banner');

                if (punchoutBanner && capHeader) {
                    const height = punchoutBanner?.offsetHeight;
                    capHeader.style.top = `${height}px`;
                }
            };

            capObserver = new IntersectionObserver(entries => {
                const isSticky = !entries[0]?.isIntersecting;
                capHeader?.classList?.toggle('sticking', isSticky);
                if (isSticky) {
                    adjustHeaderPosition(); // Adjust top position when sticky
                } else if (capHeader) {
                    capHeader.style.top = '0'; // Reset top when it is not sticky
                }
            });

            // Start observing the scrollWatcher
            capObserver.observe(scrollWatcher);
        } catch (error) {
            console.error('An error occurred during IntersectionObserver setup:', error);
        }

        // Cleanup function to disconnect the observer when the component is unmounted
        return () => {
            try {
                capObserver?.disconnect();
                scrollWatcher?.remove(); // Remove the watcher element if needed
            } catch (error) {
                console.error('An error occurred during cleanup:', error);
            }
        };
    }, []); // Empty dependency array ensures the effect runs only once

    useEffect(() => {
        const cartLoader = document?.querySelector('.cart__loader');
        if (cartLoader) {
            cartLoader.style.display = 'none';
        }
        sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.CHECKOUTUSERDETAILS);
        sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.CART_ITEMS_AVAILABILITY_CHANGED_REASON);
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        if (scrollToUnavailableSection) {
            const unavailableSection = document.getElementById('unavailable-rentals');
            unavailableSection?.scrollIntoView();
            // resetting value to use again later
            setScrollToUnavailableSection(false);
        }
    }, [scrollToUnavailableSection]);
    
    useEffect(() => {
        const mainBody = document.querySelector('body');
        mainBody?.classList?.add('body--cart-page');
        try {
            const cartLoadFunction = async () => {
                if (isProfileLoaded) {
                    if (cartId) {
                        await initialiseCart();
                        setIsCartInitialised(true);
                    } else {
                        setIsCartInitialised(true);
                    }
                }
            };
            cartLoadFunction();
        } catch (error) {
            logError(error, false, 'cartLoadFunction');
        }
    }, [isProfileLoaded, cartId]);

    useEffect(() => {
        const updateCart = async () => {
            setRecomputeSourcesOnContinueCheckout(false);
            await initialiseCart();
            dispatch({ type: SET_RECOMPUTE_ITEM_AVAILABILITY, recomputeItemsAvailability: false });
        };
        if (recomputeItemsAvailability) {
            updateCart();
            accessoriesAddonsCoachmark.current = null;
            toggleIsConsumablesCoachmarkOpen(false);
        }
    }, [recomputeItemsAvailability]);

    useEffect(() => {
        if (isEstimateUpdateRequired) {
            recomputeEstimates(viewCart?.pc, viewCart?.pcLat, viewCart?.pcLong, availableItemArr);
            dispatch({ type: SET_IS_ESTIMATE_UPDATE_REQUIRED, isEstimateUpdateRequired: false });
        }
    }, [isEstimateUpdateRequired]);

    useEffect(() => {
        if (viewCart?.jobSiteZip || projectDetails?.selectedProjectZip) {
            validateTimezoneAPI();
        }
        // zip is used bcoz there are 2 or more cities in US with different timezone.
    }, [viewCart?.jobSiteZip, projectDetails?.selectedProjectZip]);

    useEffect(() => {
        if (triggerAnalyticsCartEvent) {
            triggerCartViewedEvent(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CART_VIEWED,
                additionalPayload,
                availableItemArr,
                conditionallyAvailableArr,
                unavailableItemsArr
            );
            if (conditionallyAvailableArr?.length > 0 || unavailableItemsArr?.length > 0) {
                sendEventsForViewItemList(
                    EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_VIEW_SIMILAR_EQUIPMENT,
                    payloadEcommerceViewItemList([...conditionallyAvailableArr, ...unavailableItemsArr], false, true)
                );
            }
            setTriggerAnalyticsCartEvent(false);
        }
    }, [triggerAnalyticsCartEvent]);

    useEffect(() => {
        if (isAllItemsUnavailableModalOpen) {
            additionalPayload = {
                form_name: EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_ALL_ITEMS_UNAVAILABLE,
                form_type: EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_INFORMATION
            };
            triggerCartViewedEvent(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_VIEWED,
                additionalPayload,
                availableItemArr,
                conditionallyAvailableArr,
                unavailableItemsArr
            );
            if (conditionallyAvailableArr?.length > 0 || unavailableItemsArr?.length > 0) {
                sendEventsForViewItemList(
                    EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_VIEW_SIMILAR_EQUIPMENT,
                    payloadEcommerceViewItemList([...conditionallyAvailableArr, ...unavailableItemsArr], false, true)
                );
            }
        }
    }, [isAllItemsUnavailableModalOpen]);

    useEffect(() => {
        if (isConsumablesCoachmarkOpen) {
            const coachmark = document?.querySelector(`.consumables__tooltip_${accessoriesAddonsCoachmark?.current}`);
            if (coachmark) {
                coachmark?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
                coachmark?.focus();
            }
        }
    }, [isConsumablesCoachmarkOpen, accessoriesAddonsCoachmark?.current]);

    const initialiseCart = async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const cartDetails = await renderCartItems();
        const { consumablesData, ...cartItems } = cartDetails || {};
        const { pc, pcLat, pcLong } = viewCart || {};
        updateItemsAndEstimates(
            cartItems,
            pc,
            pcLat,
            pcLong,
            consumablesData,
            abortControllerRef?.current?.signal
        ).then(() => {
            // we need estimates to complete for resetting recomputeItemsAvailability
            // case - changes in cap will change estimates so loading should stop after estimates update
            dispatch({ type: SET_RECOMPUTE_ITEM_AVAILABILITY, recomputeItemsAvailability: false });
        });
    };

    const updateItemsAndEstimates = async (cartItems, pc, pcLat, pcLong, consumablesData, signal) => {
        try {
            const { availableItems, unavailableItems, conditionalItems, cartResponse } =
                cartItems || {};

            /** updating in state without waiting for estimates
             *  so the change in product status is immediately visible
             */
            let updatedAvailableItems = getAvailableItemStatusFromATP(availableItems);
            setAvailableItemArr(updatedAvailableItems || []);
            setConditionallyAvailableArr(conditionalItems || []);
            setUnavailableItemsArr(unavailableItems || []);

            /** making estimates call and updating only available items,
             *  so the correct estimates are shown for all available items.
             *  Issue fixed - cart id call do not have estimates for products
             *  which are recently added or was moved to available section
             */
            const cartEstimates = await recomputeEstimates(
                pc,
                pcLat,
                pcLong,
                availableItems,
                consumablesData,
                signal,
                cartResponse
            );
            if (cartEstimates?.availableCartItems?.length > 0) {
                updatedAvailableItems = getAvailableItemStatusFromATP(cartEstimates?.availableCartItems);
                setAvailableItemArr(updatedAvailableItems || []);
            }
            setTriggerAnalyticsCartEvent(true);
        } catch (error) {
            logError(error, false, 'updateItemsAndEstimates');
        }
    };

    /** for p2p we have status of AVAILABLE_WITH_WARNING for some items */
    const getAvailableItemStatusFromATP = availableItems => {
        if (authorityType === AUTHORITY_TYPE.P2P) {
            return availableItems?.map(item => {
                const tileStatus = handleATPCart({
                    catsku: item?.product?.sku,
                    inventoryDataObj: item?.product?.ec_pc_inventory
                });
                return {
                    ...item,
                    tileStatus
                };
            });
        }
        return availableItems;
    };

    const updateEstimatesFromDeliverySourcePc = async (chronosStore, availableItems, consumablesData) => {
        // this is to fetch estimates when sources gives different pc for delivery
        // and the cart items availability has not changed.
        const cartEstimates = await recomputeEstimates(
            chronosStore?.pc,
            chronosStore?.latitude,
            chronosStore?.longitude,
            availableItems,
            consumablesData
        );
        if (cartEstimates?.availableCartItems?.length > 0) {
            const updatedAvailableItems = getAvailableItemStatusFromATP(cartEstimates?.availableCartItems);
            setAvailableItemArr(updatedAvailableItems || []);
        }
        setTriggerAnalyticsCartEvent(true);
    };

    const handleCheckoutValidationAndRedirect = async ({
        updatedViewCart, // pass this if viewCart value is updated
        availableCartItems, // pass this if cart available items are updated
        recomputeSourcesData = recomputeSourcesOnContinueCheckout
    }) => {
        const {
            isErrorFromAvs,
            isStoreUnavailableOrError,
            unavailableItemsFromSourcesDetails,
            nextAvailableSlot,
            endDateError,
            pickupSlotUnavailableDate
        } = await onCheckoutHandler({
            updatedViewCart,
            availableCartItems,
            updateEstimatesFromDeliverySourcePc,
            recomputeSourcesData
        });
        if (isErrorFromAvs) {
            // handle avs error
            const avsResponse = JSON.parse(sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.AVS_RESPONSE) || '{}');
            if (isObjectWithKeys(avsResponse)) {
                setShowAvsModal(true);
            }
        } else if (isStoreUnavailableOrError) {
            // when no pc is available to fulfill cart items
            setIsAllItemsUnavailableModalOpen(true);
        } else if (unavailableItemsFromSourcesDetails) {
            // when pc is present but some items are not present in any of the pc
            setUnavailableItemsFromSources(unavailableItemsFromSourcesDetails);
            dispatch({
                type: SET_CART_ITEM_STATUS_CHANGED_REASON,
                cartItemsAvailabilityChangedReason:
                    VARIABLE_CONFIG.CART_ITEM_STATUS_CHANGED_REASON.CHANGED_FROM_CHRONOS_STORE
            });
            sessionStorage.setItem(
                STORAGE_CONFIG.SESSION_STORAGE.CART_ITEMS_AVAILABILITY_CHANGED_REASON,
                VARIABLE_CONFIG.CART_ITEM_STATUS_CHANGED_REASON.CHANGED_FROM_CHRONOS_STORE
            );
        } else if (pickupSlotUnavailableDate) {
            // if no time slot available for pickup or return for fufillment type inStorePickup.
            setNoAvailablePickupDate(pickupSlotUnavailableDate);
            setShowDeliverySlotModal(true);
        } else if (nextAvailableSlot) {
            // if nextAvailableSlot is available then we will show no delivery slot modal for fufillment type delivery.
            setAvailableStartDate(nextAvailableSlot);
            setShowDeliverySlotModal(true);
        } else if (endDateError) {
            // if endDate is not available then we will show no pickup slot modal for fufillment type delivery.
            setShowDeliverySlotModal(true);
        }
    };

    const handleCheckoutSignInDrawer = async (updatedViewCart, availableCartItems, recomputeSourcesData) => {
        if (userType === USER_TYPE.GUEST) {
            setIsCheckoutDrawerOpen(true);
            setRecomputeSourcesOnContinueCheckout(recomputeSourcesData);
        } else {
            handleCheckoutValidationAndRedirect({ updatedViewCart, availableCartItems, recomputeSourcesData });
        }
    };

    const validateAvailablePickupItemFromSources = () => {
        if (sortedChronosPcs) {
            if (showDiscrepencyStoreSelector(availableItemArr)) {
                // logic to show store selector modal if chronos suggest
                // different store from the selected store or some items are unavailable
                const { selectedStore, suggestedStores, allItemsUnavailableInAllStores } =
                    getSelectedAndSuggestedStores(unavailableItemsArr, cart?.items);
                let isUnavailableItems =
                    selectedStore?.unavailableProducts?.length > 0 ||
                    suggestedStores?.some(
                        storeWithAvailability => storeWithAvailability?.unavailableProducts?.length > 0
                    ) ||
                    false;
                //checking if there is no availability for the selected store and there is no suggested stores and all the items are unavailable then show modal
                if (allItemsUnavailableInAllStores) {
                    setIsAllItemsUnavailableModalOpen(true);
                } else {
                    setSelectedLocationStore(selectedStore);
                    setSuggestedChronosStores(suggestedStores);
                    toggleStoreSelectorDrawer(false);
                    triggerModalViewedEvent(isUnavailableItems);
                }
            } else if (sortedChronosPcs?.length === 0) {
                // no available chronos stores
                setIsAllItemsUnavailableModalOpen(true);
            } else {
                // for updating the sources data in the selected store
                // filterDispatch({ type: SET_SELECTED_STORE_DETAILS, selectedStoreDetails: sortedChronosPcs[0] });
                handleCheckoutSignInDrawer();
            }
        } else {
            handleCheckoutSignInDrawer();
        }
    };

    const handleConsumableCoachmark = () => {
        const isOptionalConsumableNotSelected = isAvailableOptionalItemsNotSelected(availableItemArr);
        if (isOptionalConsumableNotSelected) {
            toggleIsAdditionalConsumableModalOpen();
        } else if (viewCart?.isInStorePickup) {
            validateAvailablePickupItemFromSources();
        } else {
            handleCheckoutSignInDrawer();
        }
    };

    const handleCheckoutBtnClick = async () => {
        if (!isRentalDetailsAvailable() || (!startDate && !endDate)) {
            openEmptyLocationDateModal();
            return;
        }
        if (isAccountClosed(userAccount)) {
            dispatch({
                type: SET_SHOW_DELINQUENT_ACCOUNT_MODAL,
                showDelinquentModal: true
            });
            return;
        }
        triggerCheckoutStartEvent();
        handleConsumableCoachmark();
    };

    const isSourcesCallRequired = (cartItems, storeDetailData) => {
        if (storeDetailData?.fulfillmentPercent == 1) {
            return false;
        } else if (cartItems?.availableItems?.length !== availableItemArr?.length) {
            return true;
        }
        // checking if there is any difference in available cart items
        return cartItems?.availableItems?.find(
            item => !availableItemArr?.find(availableItem => availableItem?.pc === item?.pc)
        );
    };

    const handleChronosStoreSelector = async storeDetailData => {
        try {
            // storeDetailData will be empty if the user has not selected any store
            if (isObjectWithKeys(storeDetailData)) {
                const updatedViewCart = {
                    ...viewCart,
                    pc: storeDetailData?.pc,
                    pcLat: storeDetailData?.latitude,
                    pcLong: storeDetailData?.longitude
                };
                const cartItemsChangedReason =
                    storeDetailData?.pc === selectedStoreDetails?.pc
                        ? VARIABLE_CONFIG.CART_ITEM_STATUS_CHANGED_REASON.CHANGED_FROM_CHRONOS_STORE
                        : VARIABLE_CONFIG.CART_ITEM_STATUS_CHANGED_REASON.STORE_CHANGED_FROM_DISCREPENCY_DRAWER;
                filterDispatch({ type: SET_SELECTED_STORE_DETAILS, selectedStoreDetails: storeDetailData });
                filterDispatch({ type: SET_LOCATION_DATA, ...updatedViewCart });
                updateSourceAndLocationSearchRadius(storeDetailData?.distanceFromJobSite);
                dispatch({
                    type: SET_CART_ITEM_STATUS_CHANGED_REASON,
                    cartItemsChangedReason: cartItemsChangedReason
                });
                sessionStorage.setItem(
                    STORAGE_CONFIG.SESSION_STORAGE.CART_ITEMS_AVAILABILITY_CHANGED_REASON,
                    cartItemsChangedReason
                );
                localStorage.setItem(
                    STORAGE_CONFIG.LOCAL_STORAGE.SELECTEDSTOREDETAILS,
                    JSON.stringify(storeDetailData)
                );
                sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.CART_ITEMS);
                if (storeDetailData?.pc === selectedStoreDetails?.pc && !isWithin72Hours()) {
                    /** this scenario comes when the sources does not give the selected store for >72hrs.
                     *  since sources has not recommended the selected store, will show all items unavailable modal
                     **/
                    setIsAllItemsUnavailableModalOpen(true);
                } else {
                    const cartItems = updateItemsFromSelectedChronosStore({ selectedChronosStore: storeDetailData });
                    const consumablesData = await getSelectedConsumablesAvailability(
                        cartItems?.availableItems,
                        storeDetailData?.pc,
                        true
                    );
                    await bulkUpdateConsumables(consumablesData?.updatedSelectedConsumables, cartItems?.availableItems);
                    updateItemsAndEstimates(
                        cartItems,
                        storeDetailData?.pc,
                        storeDetailData?.latitude,
                        storeDetailData?.longitude,
                        consumablesData
                    );
                    if (cartItems?.availableItems?.length > 0) {
                        handleCheckoutSignInDrawer(
                            updatedViewCart,
                            cartItems?.availableItems,
                            isSourcesCallRequired(cartItems, storeDetailData)
                        );
                    }
                }
                toggleStoreSelectorDrawer(false);
                triggerAvsModalCtaEvent(storeDetailData, selectedLocationStore);
            }
        } catch (error) {
            logError(error, false, 'handleChronosStoreSelector', [storeDetailData]);
        }
    };

    const toggleStoreSelectorDrawer = (isCloseClicked = true) => {
        setShowStoreSelector(!showStoreSelector);
        if (showStoreSelector && isCloseClicked) {
            triggerModalCancelEvent();
        }
    };

    const toggleIsCheckoutDrawerOpen = () => {
        setIsCheckoutDrawerOpen(!isCheckoutDrawerOpen);
    };

    const onContinueAsGuestHandler = async () => {
        toggleIsCheckoutDrawerOpen();
        handleCheckoutValidationAndRedirect({});
    };

    const toggleShowAvsModal = () => {
        setShowAvsModal(!showAvsModal);
    };

    const onContinueWithAvsAddress = updatedViewCart => {
        toggleShowAvsModal();
        handleCheckoutValidationAndRedirect({ updatedViewCart });
    };

    const toggleDeliverySlotAlertModal = () => {
        setShowDeliverySlotModal(!showDeliverySlotModal);
    };

    const resetUnavailableItemsFromSourcesData = () => {
        setUnavailableItemsFromSources([]);
    };

    const toggleIsAllItemsUnavailableModalOpen = () => {
        setIsAllItemsUnavailableModalOpen(!isAllItemsUnavailableModalOpen);
    };

    const updateItemsStatusFromSources = () => {
        const sourcesData = sortedChronosPcs[0];
        const cartItems = updateItemsFromSelectedChronosStore({
            selectedChronosStore: sourcesData
        });
        resetUnavailableItemsFromSourcesData();
        return cartItems || {};
    };

    const handleReviewPartialUnavailableItems = async () => {
        try {
            const cartItems = updateItemsStatusFromSources();
            // updating items in state
            const { availableItems, unavailableItems, conditionalItems } = cartItems || {};
            let updatedAvailableItems = getAvailableItemStatusFromATP(availableItems);
            setAvailableItemArr(updatedAvailableItems || []);
            setConditionallyAvailableArr(conditionalItems || []);
            setUnavailableItemsArr(unavailableItems || []);
            setScrollToUnavailableSection(true);
            const [chronosStoresResp, selectedConsumablesResp] = await Promise.allSettled([
                handleChronosStores(cartItems),
                getSelectedConsumablesAvailability(availableItems, sortedChronosPcs?.[0]?.pc, true)
            ]);
            const firstChronosStore = chronosStoresResp?.value?.[0];
            const consumablesData = selectedConsumablesResp?.value || {};
            bulkUpdateConsumables(consumablesData?.updatedSelectedConsumables, availableItems);
            // fetching estimates and updating in state
            updateItemsAndEstimates(
                cartItems,
                firstChronosStore?.pc,
                firstChronosStore?.latitude,
                firstChronosStore?.longitude,
                consumablesData
            );
        } catch (error) {
            logError(error, false, 'handleReviewPartialUnavailableItems');
        }
    };

    const handleContinueWithPartialUnavailableItems = async () => {
        try {
            const cartItems = updateItemsStatusFromSources();
            updateItemsAndEstimates(cartItems); // not passing pc so that estimates are not called
            if (cartItems?.availableItems?.length > 0) {
                handleCheckoutValidationAndRedirect({
                    recomputeSourcesData: true,
                    availableCartItems: cartItems?.availableItems
                });
            }
        } catch (error) {
            logError(error, false, 'handleContinueWithPartialUnavailableItems');
        }
    };

    const isUnavailableItemsAvailable = () => {
        return cart?.availableCartItems?.length != cart?.items?.length;
    };

    const onLinkLabelClick = anchorTagName => {
        triggerEventForViewDetailsCta(anchorTagName);
    };

    const onPlaceInStorePickup = async () => {
        try {
            openModal(CHOOSE_STORE, {
                showShimmer: true,
                availableStores: null,
                showInDrawerForTablet: true,
                showDrawerInRight: true,
                showMonochromaticMap: true
            });
            const chronosStores = await fetchChronosForInStorePickup(availableItemArr, conditionallyAvailableArr);
            const { cartItemsForChronosStoresWithAtp, conditionalAndUnavailableItemsPerStore } =
                getCartItemsForChronosStoresWithAtp(chronosStores);
            openModal(CHOOSE_STORE, {
                availableStores: { data: chronosStores },
                showInDrawerForTablet: true,
                showDrawerInRight: true,
                showMonochromaticMap: true,
                localUnavailableCartItemsPerPc: conditionalAndUnavailableItemsPerStore,
                skipCartUpdate: true,
                showAlertModal: true,
                onStoreSelect: selectedStoreFromModal =>
                    onStoreSelect(cartItemsForChronosStoresWithAtp, selectedStoreFromModal)
            });
        } catch (err) {
            logError(err, false, 'onPlaceInStorePickup');
        }
    };

    const onStoreSelect = async (cartItemsForChronosStoresWithAtp, storeSelected) => {
        try {
            toggleIsCheckoutDrawerOpen();
            if (!storeSelected?.pc) return;
            updateFulfillmentToPickup(storeSelected);
            updateSourceAndLocationSearchRadius(storeSelected?.distanceFromJobSite);
            const updatedItems = cartItemsForChronosStoresWithAtp[storeSelected?.pc];
            if (!updatedItems) return;
            saveCartItemsInStorage(updatedItems);
            if (!updatedItems?.availableItems?.length) {
                updateItemsAndEstimates(updatedItems, storeSelected?.pc, storeSelected?.latitude, storeSelected?.longitude);
                return;
            }
            const consumablesData = await getSelectedConsumablesAvailability(updatedItems?.availableItems, storeSelected?.pc, true);
            await bulkUpdateConsumables(consumablesData?.updatedSelectedConsumables, updatedItems?.availableItems);
            updateItemsAndEstimates(updatedItems, storeSelected?.pc, storeSelected?.latitude, storeSelected?.longitude, consumablesData);
            const updatedViewCart = {
                ...viewCart,
                isInStorePickup: true,
                pc: storeSelected?.pc,
                pcLat: storeSelected?.latitude,
                pcLong: storeSelected?.longitude
            };
            handleCheckoutValidationAndRedirect({
                updatedViewCart,
                availableCartItems: updatedItems?.availableItems,
                recomputeSourcesData: true
            });
        } catch (err) {
            logError(err, false, 'onStoreSelect');
        }
    };

    const onSkippingOptionalItems = () => {
        toggleIsAdditionalConsumableModalOpen();
        if (viewCart?.isInStorePickup) {
            validateAvailablePickupItemFromSources();
        } else {
            handleCheckoutSignInDrawer();
        }
        try {
            sendEventsForCtaClicked(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CTA_CLICKED,
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_SKIP_ADDING_ITEMS,
                VARIABLE_CONFIG.TYPE.BUTTON,
                VARIABLE_CONFIG.EVENT_LINK_LOCATION.BODY,
                VARIABLE_CONFIG.ECOMMERCE_PAGE.CHECKOUT
            );
        } catch (error) {
            logError(error, false, 'onSkippingOptionalItems');
        }
    };

    const onBackFromOptionalModal = (isBackClicked = false) => {
        toggleIsAdditionalConsumableModalOpen();
        toggleIsConsumablesCoachmarkOpen(true);
        try {
            sendEventsForCtaClicked(
                EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CTA_CLICKED,
                isBackClicked
                    ? EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_GO_BACK_TO_CART
                    : EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CLOSE,
                VARIABLE_CONFIG.TYPE.BUTTON,
                VARIABLE_CONFIG.EVENT_LINK_LOCATION.BODY,
                VARIABLE_CONFIG.ECOMMERCE_PAGE.CART
            );
        } catch (error) {
            logError(error, false, 'onBackFromOptionalModal');
        }
    };

    const toggleIsConsumablesCoachmarkOpen = isOpen => {
        if (isValidString(accessoriesProperties?.showCoachMark)) {
            dispatch({ type: SET_IS_CONSUMABLES_COACHMARK_OPEN, isConsumablesCoachmarkOpen: isOpen });
        }
    };

    const skipConsumablesWarningQuestion = () => {
        return (
            <div 
                className='skip-consumables-content'
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(accessoriesProperties?.skipAccessoriesModalDescr || intl.formatMessage({ id: 'additional-consumables-modal:warning' })) }} 
            />
        );
    }

    const getWeekendRentalContent = () => {
        return (
            <>
                <h5 className="weekend_rental-title">
                    {cartAlertProperties?.weekendRentalMessageTitle || AUTHORABLE_FALLBACK?.weekendRentalTitle}
                </h5>
                <p className="weekend_rental-content">
                    {cartAlertProperties?.weekendRentalMessageText || AUTHORABLE_FALLBACK?.weekendRentalMessage}
                </p>
            </>
        );
    };

    if (isCartInitialised) {
        if (isEmpty) {
            return (
                <LazyLoadComponent
                    fallback={
                        <div className="cart-loader">
                            <LoadingIndicator />
                        </div>
                    }>
                    <EmptyCartView />
                </LazyLoadComponent>
            );
        } else {
            return (
                <>
                    <LazyLoadComponent fallback={<CartPageShimmer />}>
                        <section className="cart-page__content">
                            <div className="container">
                                <div className="row">
                                    {/* isCheckoutDetailsValidating: Might be removed after future rework */}
                                    {(isCartLoading ||
                                        recomputeItemsAvailability ||
                                        isCheckoutDetailsValidating) && (
                                        <div className="cart-loader">
                                            <LoadingIndicator global />
                                        </div>
                                    )}
                                    <div className="col-12 cart-page__left">
                                        <h1 className="block-cart-title">
                                            {cartGenericProperties?.title || AUTHORABLE_FALLBACK?.cartTitle}
                                        </h1>
                                        {shouldShowAlertMessage &&
                                            (unavailableItemsArr?.length > 0 || conditionallyAvailableArr?.length > 0) && (
                                                <div className="no-longer-available-error" aria-live="polite">
                                                    <Alert
                                                        message={
                                                            cartAlertProperties?.alertMessage ||
                                                            AUTHORABLE_FALLBACK?.alertMessage
                                                        }
                                                        type="error"
                                                        role={'none'}
                                                        icon={<AlertIcon tabIndex={-1} aria-hidden={true} />}
                                                        ariaLabel={
                                                            cartAlertProperties?.ariaLabelContent ||
                                                            AUTHORABLE_FALLBACK?.ariaLabelContent
                                                        }
                                                        anchorTagName={
                                                            cartAlertProperties?.ctaLabel || AUTHORABLE_FALLBACK?.ctaLink
                                                        }
                                                        tabIndex={-1}
                                                        noRole={true}
                                                        id={'cart-error-message-id'}
                                                        onLinkLabelClick={onLinkLabelClick}
                                                        isAriaHidden={true}
                                                    />
                                                </div>
                                            )}
                                        {showWeekendRentalAlert() && !isP2PUser && (
                                            <div className="weekend_rental-container" aria-live="polite">
                                                <Alert
                                                    message={getWeekendRentalContent()}
                                                    type="success"
                                                    dataTestId="weekend_rental_testId"
                                                    role="alert"
                                                    icon={<AlertRightCircle tabIndex={-1} aria-hidden={true} />}
                                                    ariaLabel={
                                                        cartAlertProperties?.weekendRentalMessageText ||
                                                        AUTHORABLE_FALLBACK?.weekendRentalMessage
                                                    }
                                                    tabIndex={-1}
                                                    customClass="weekend_rental-wrap"
                                                />
                                            </div>
                                        )}
                                        <div className="cart-page__list">
                                            <CartItems
                                                unavailableItemsArr={unavailableItemsArr}
                                                conditionallyAvailableArr={conditionallyAvailableArr}
                                                availableItemArr={availableItemArr}
                                                handleCartQuantityUpdation={handleCartQuantityUpdation}
                                                abortControllerRef={abortControllerRef}
                                                accessoriesAddonsCoachmark={accessoriesAddonsCoachmark}
                                                hasCartLoaded={
                                                    !(
                                                        isCartLoading ||
                                                        recomputeItemsAvailability ||
                                                        isCheckoutDetailsValidating
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12 cart-page__right">
                                        <div className="cart-page__right-wrap">
                                            <CartOrderSummary />
                                            <div className="cart-footer-section">
                                                <CartFooter
                                                    handleCheckoutBtnClick={handleCheckoutBtnClick}
                                                    availableItemArr={availableItemArr}
                                                    unavailableItemsArr={unavailableItemsArr}
                                                    conditionallyAvailableArr={conditionallyAvailableArr}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </LazyLoadComponent>
                    <LazyLoadComponent
                        fallback={
                            <div className="cart-loader">
                                <LoadingIndicator global />
                            </div>
                        }>
                        {showStoreSelector && (
                            <ChronosStoreSelectorDrawer
                                toggleStoreSelectorDrawer={toggleStoreSelectorDrawer}
                                suggestedChronosStores={suggestedChronosStores}
                                selectedLocationStore={selectedLocationStore}
                                onStoreSelect={handleChronosStoreSelector}
                            />
                        )}
                        {isCheckoutDrawerOpen && (
                            <CartDrawerGuestCheckout
                                isCheckoutDrawerOpen={isCheckoutDrawerOpen}
                                toggleIsCheckoutDrawerOpen={toggleIsCheckoutDrawerOpen}
                                onContinueAsGuestHandler={onContinueAsGuestHandler}
                                onPlaceInStorePickup={onPlaceInStorePickup}
                            />
                        )}
                        {showAvsModal && (
                            <AvsModal
                                toggleShowAvsModal={toggleShowAvsModal}
                                onContinueWithAvsAddress={onContinueWithAvsAddress}
                            />
                        )}
                        {showDeliverySlotModal && (
                            <DeliverySlotAlertModal
                                isOpen
                                toggleDeliverySlotAlertModal={toggleDeliverySlotAlertModal}
                                availableStartDate={availableStartDate}
                                noAvailablePickupDate={noAvailablePickupDate}
                                unavailableItemsAvailable={isUnavailableItemsAvailable()}
                            />
                        )}
                        {unavailableItemsFromSources?.length > 0 && (
                            <PartialItemsUnavailableModal
                                isOpen
                                productList={unavailableItemsFromSources}
                                handleModalClose={resetUnavailableItemsFromSourcesData}
                                onReviewItems={handleReviewPartialUnavailableItems}
                                onContinue={handleContinueWithPartialUnavailableItems}
                            />
                        )}
                        {isAllItemsUnavailableModalOpen && (
                            <AllItemsUnavailableModal isOpen toggleModal={toggleIsAllItemsUnavailableModalOpen} />
                        )}
                        {isAdditionalConsumableModalOpen && (
                            <AlertModalWithButtons
                                isOpen
                                title={ accessoriesProperties?.skipAccessoriesModalTitle || intl.formatMessage({ id: 'additional-consumables-modal:title' })}
                                warningQuestion={skipConsumablesWarningQuestion()}
                                handleContinueClick={onSkippingOptionalItems}
                                continueButtonText={accessoriesProperties?.primarySkipButtonLabel || intl.formatMessage({
                                    id: 'additional-consumables-modal:continue-btn'
                                })}
                                handleCancelClick={onBackFromOptionalModal}
                                cancelButtonText={accessoriesProperties?.secondarySkipButtonLabel || intl.formatMessage({
                                    id: 'additional-consumables-modal:cancel-btn'
                                })}
                            />
                        )}
                        {isConsumablesCoachmarkOpen && (
                            <StorybookCoachmark
                                isVisible={isConsumablesCoachmarkOpen}
                                anchorSelect={`.consumables__tooltip_${accessoriesAddonsCoachmark.current}`}
                                place="bottom-start"
                                handleCoachmarkClose={() => toggleIsConsumablesCoachmarkOpen(false)}
                                content={
                                    accessoriesProperties?.coachMarkText ||
                                    intl.formatMessage({ id: 'consumables:coachmark-content' })
                                }></StorybookCoachmark>
                        )}
                        {consumables?.showRentalEquipmentScreen && <RentalEquipmentView />}
                    </LazyLoadComponent>
                </>
            );
        }
    } else {
        return <CartPageShimmer />;
    }
};

export default React.memo(Cart);
